import { useMemo } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Card, Tag, Typography } from 'antd';
import cn from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import {
  type IMainPageVersion,
  usePublishMainPageMutation,
  useUpdateMainPageMutation,
} from '@/api/endpoints/mainPages';
import { Title } from '@/shared/components';
import { CheckboxField } from '@/shared/components/Fields/CheckboxField';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { EMainPageStatusToText } from '../consts';
import { transformDateToCorrectFormat } from '../helpers';

import { getBlocksData } from './helpers';
import styles from './MainEditPage.module.scss';
import type { IFormData } from './types';

interface IProp {
  pageData: IMainPageVersion;
}

export const MainEditPageContent = ({ pageData }: IProp) => {
  const { id: pageId, active: isPageActive, created_at: pageCreatedDate } = pageData;
  const [publishPage, { isLoading: isPagePublishing }] = usePublishMainPageMutation();
  const [updatePage, { isLoading: isPageUpdating }] = useUpdateMainPageMutation();

  const navigate = useNavigate();

  const sendApiRequest = useApiRequest();

  const blocksData = useMemo(() => getBlocksData(pageData), [pageData]);

  const methods = useForm<IFormData>({
    defaultValues: blocksData?.reduce(
      (acc, { fieldName, visible }) => {
        acc[fieldName] = visible;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  });

  const { handleSubmit } = methods;

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`);
  };

  const savePageHandler = handleSubmit((data) => {
    const {
      aboutSchool: isAboutSchoolVisible,
      advantages: areAdvantagesVisible,
      reviews: areReviewsVisible,
      aboutBank: isAboutBankVisible,
      faqs: areFAQsVisible,
      contact: isContactVisible,
    } = data;

    sendApiRequest({
      successMessage: 'Версия успешно сохранена',
      apiRequestCallback: () =>
        updatePage({
          id: pageId,
          data: {
            about_school: isAboutSchoolVisible,
            advantages: areAdvantagesVisible,
            reviews: areReviewsVisible,
            about_bank: isAboutBankVisible,
            faqs: areFAQsVisible,
            contact: isContactVisible,
          },
        }),
    });
  });

  const publishPageHandler = async () => {
    await sendApiRequest({
      successMessage: 'Версия успешно опубликована',
      apiRequestCallback: () => publishPage({ id: pageId }),
    });
  };

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: `Версия ${transformDateToCorrectFormat(pageCreatedDate)}` },
        ]}
      />
      <div className={styles['status-wrapper']}>
        <Title>
          Статус :{' '}
          <span className={cn(styles.status, { [styles['status-unpublished']]: !isPageActive })}>
            {isPageActive
              ? EMainPageStatusToText.Published.toUpperCase()
              : EMainPageStatusToText.UnPublished.toUpperCase()}
          </span>
        </Title>
      </div>
      <Title>Блоки страницы :</Title>
      <FormProvider {...methods}>
        <div className={styles['content-wrapper']}>
          {blocksData?.map(({ id, name, required, filled, slug, fieldName }) => {
            const statusName = (filled ? 'Заполнено' : 'Не заполнено').toUpperCase();
            return (
              <Card
                key={id}
                classNames={{ body: styles['card-wrapper'] }}
              >
                <div className={cn(styles['title-wrapper'], { [styles['title-wrapper-required']]: required })}>
                  <CheckboxField
                    className={cn({ [styles.hidden]: required })}
                    fieldName={fieldName}
                    disabled={required}
                  >
                    <Typography.Text strong>{name}</Typography.Text>
                  </CheckboxField>
                </div>
                <div className={styles['block-status-wrapper']}>
                  <Tag
                    color={filled ? 'green' : 'volcano'}
                    className={styles.tag}
                  >
                    {statusName}
                  </Tag>
                </div>
                <Button
                  className={styles['dropdown-button']}
                  size="middle"
                  onClick={() => navigate(slug)}
                >
                  <RightOutlined />
                </Button>
              </Card>
            );
          })}
          <div className={styles['buttons-wrapper']}>
            <Button
              onClick={goBack}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
            <div className={styles['actions-buttons-wrapper']}>
              <Button
                onClick={savePageHandler}
                size="large"
                className={styles.button}
                type={isPageActive ? 'primary' : 'default'}
                disabled={!blocksData[0].filled || !blocksData[1].filled || isPagePublishing || isPageUpdating}
              >
                Сохранить
              </Button>
              {!isPageActive && (
                <Button
                  onClick={publishPageHandler}
                  type="primary"
                  size="large"
                  className={styles.button}
                  disabled={!blocksData[0].filled || !blocksData[1].filled || isPagePublishing || isPageUpdating}
                >
                  Опубликовать
                </Button>
              )}
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
